import React, { useState, useEffect, useRef } from 'react';
import { 
  MenuBook, 
  Translate, 
  ViewList,
  ViewModule,
  TrendingUp,
  CheckCircle,
  Star,
  Speed,
  Close
} from '@mui/icons-material';
import { jwtDecode } from 'jwt-decode';
import languageLearningApi from '../middleware/languageLearningApi';
import generalApi from '../../../generalApi';
import { useNavigation } from '../../../context/NavigationContext';
import MultiStepCardViewer from "../../../components/srs/MultiCardStepViewer";

// Course ID to language mapping
const CourseIdToWebkitLanguage = {
  "67cdeb80b567cb0052c62148": "de-DE",
  "67ff634626c001d218da332b": "es-ES",
  "6801e9392e9e345122975c83" : "fr-FR"
}

const FlashcardProgressCard = ({ token }) => {
  const { currentCourse } = useNavigation();
  const [data, setData] = useState({ sentences: [], concepts: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAllSentences, setShowAllSentences] = useState(false);
  const [showAllConcepts, setShowAllConcepts] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  
  // Card viewer state
  const [isCardViewerOpen, setIsCardViewerOpen] = useState(false);
  const [currentCard, setCurrentCard] = useState(null);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [cardType, setCardType] = useState('sentence');
  const [cardCache, setCardCache] = useState(new Map());
  
  const cardViewerRef = useRef(null);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return 'Yesterday';
    if (diffDays <= 7) return `${diffDays} days ago`;
    
    return `Learnt: ${date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    })}`;
  };

  const checkScreenSize = () => {
    const width = window.innerWidth;
    setIsMobile(width < 768);
    setIsTablet(width >= 768 && width < 1024);
  };

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isCardViewerOpen) {
        setIsCardViewerOpen(false);
      }
    };

    if (isCardViewerOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isCardViewerOpen]);

  useEffect(() => {
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        if (!token || !currentCourse?._id) {
          throw new Error('Missing authentication token or course information');
        }

        const decoded = jwtDecode(token);
        const userId = decoded.user._id;
        const courseId = currentCourse._id;
        
        const response = await languageLearningApi.get(
          `/flashcard-mappings/details?userId=${userId}&courseId=${courseId}`
        );
        
        setData(response.data || { sentences: [], concepts: [] });
      } catch (err) {
        console.error('Error fetching flashcard data:', err);
        setError('Failed to load progress data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token, currentCourse]);

  // Card functionality
  const fetchCardData = async (cardId) => {
    if (cardCache.has(cardId)) {
      return cardCache.get(cardId);
    }

    try {
      const response = await generalApi.get(`/srs/multi-step-card/${cardId}`);
      const cardData = response.data.card;
      setCardCache(prev => new Map(prev).set(cardId, cardData));
      return cardData;
    } catch (error) {
      console.error('Error fetching card data:', error);
      throw error;
    }
  };

  const handleSentenceClick = async (sentence, index) => {
    try {
      const sentenceId = sentence.sentenceId;
      let cardId;

      const cacheKey = `sentence_${sentenceId}`;
      if (cardCache.has(cacheKey)) {
        cardId = cardCache.get(cacheKey);
      } else {
        const sentenceResponse = await languageLearningApi.get(`/sentences/${sentenceId}`);
        cardId = sentenceResponse.data.sentence.cardId;
        setCardCache(prev => new Map(prev).set(cacheKey, cardId));
      }

      const cardData = await fetchCardData(cardId);
      
      setCurrentCard(cardData);
      setCurrentCardIndex(index);
      setCardType('sentence');
      setIsCardViewerOpen(true);
    } catch (error) {
      console.error('Error opening sentence card:', error);
    }
  };

  const handleConceptClick = async (concept, index) => {
    try {
      const wordName = concept.conceptName;
      let cardId;

      const cacheKey = `concept_${wordName}`;
      if (cardCache.has(cacheKey)) {
        cardId = cardCache.get(cacheKey);
      } else {
        const vocabularyResponse = await languageLearningApi.get(
          `/vocabulary?name=${encodeURIComponent(wordName)}&language=${currentCourse.language}`
        );
        cardId = vocabularyResponse.data.vocabulary.cardId;
        setCardCache(prev => new Map(prev).set(cacheKey, cardId));
      }

      const cardData = await fetchCardData(cardId);
      
      setCurrentCard(cardData);
      setCurrentCardIndex(index);
      setCardType('concept');
      setIsCardViewerOpen(true);
    } catch (error) {
      console.error('Error opening concept card:', error);
    }
  };

  const handlePrevCard = async () => {
    if (currentCardIndex <= 0) return;
    
    const newIndex = currentCardIndex - 1;
    const items = cardType === 'sentence' ? getSentencesToShow() : getConceptsToShow();
    const item = items[newIndex];
    
    if (cardType === 'sentence') {
      await handleSentenceClick(item, newIndex);
    } else {
      await handleConceptClick(item, newIndex);
    }
  };

  const handleNextCard = async () => {
    const items = cardType === 'sentence' ? getSentencesToShow() : getConceptsToShow();
    if (currentCardIndex >= items.length - 1) return;
    
    const newIndex = currentCardIndex + 1;
    const item = items[newIndex];
    
    if (cardType === 'sentence') {
      await handleSentenceClick(item, newIndex);
    } else {
      await handleConceptClick(item, newIndex);
    }
  };

  const getSentencesToShow = () => {
    return showAllSentences ? data.sentences : data.sentences.slice(0, 10);
  };

  const getConceptsToShow = () => {
    return showAllConcepts ? data.concepts : data.concepts.slice(0, 10);
  };

  if (loading) {
    return (
      <div style={styles.card}>
        <div style={styles.loadingContainer}>
          <div style={styles.spinner}></div>
          <p style={styles.loadingText}>Loading progress...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.card}>
        <div style={styles.errorContainer}>
          <div style={styles.errorAlert}>
            <p style={styles.errorText}>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (isCardViewerOpen && currentCard) {
    return (
      <div style={styles.overlay}>
        <div style={styles.cardViewerContainer}>
          <button 
            style={styles.closeButton}
            onClick={() => setIsCardViewerOpen(false)}
            aria-label="Close card viewer"
          >
            <Close sx={{ fontSize: 24 }} />
          </button>
          <div style={styles.cardViewerContent}>
            <MultiStepCardViewer
              key={`card-${currentCard._id || currentCardIndex}`}
              ref={cardViewerRef}
              cardData={currentCard}
              language={CourseIdToWebkitLanguage[currentCourse._id]}
              onPrevCard={handlePrevCard}
              onNextCard={handleNextCard}
            />
          </div>
        </div>
      </div>
    );
  }

  const StatsSection = () => (
    <div style={dynamicStyles.statsSection}>
      <div style={dynamicStyles.statItem}>
        <div style={dynamicStyles.statRow}>
          <div style={styles.statIconContainer}>
            <MenuBook sx={{ fontSize: 32, color: '#1976d2' }} />
            <div style={styles.progressBadge}>
              <CheckCircle sx={{ fontSize: 12, color: '#4caf50' }} />
            </div>
          </div>
          <div>
            <div style={{ ...styles.statNumber, color: '#1976d2' }}>
              {data.sentences.length}
            </div>
            <div style={styles.statLabel}>
              Sentences Learnt
            </div>
          </div>
        </div>
        <div style={styles.trendingContainer}>
          <TrendingUp sx={{ fontSize: 14, color: '#4caf50' }} />
          <span style={styles.trendingText}>Growing</span>
        </div>
      </div>
      
      <div style={dynamicStyles.statItem}>
        <div style={dynamicStyles.statRow}>
          <div style={styles.statIconContainer}>
            <Translate sx={{ fontSize: 32, color: '#dc004e' }} />
            <div style={styles.progressBadge}>
              <Star sx={{ fontSize: 12, color: '#ff9800' }} />
            </div>
          </div>
          <div>
            <div style={{ ...styles.statNumber, color: '#dc004e' }}>
              {data.concepts.length}
            </div>
            <div style={styles.statLabel}>
              Words Learnt
            </div>
          </div>
        </div>
        <div style={styles.trendingContainer}>
          <TrendingUp sx={{ fontSize: 14, color: '#4caf50' }} />
          <span style={styles.trendingText}>Growing</span>
        </div>
      </div>
    </div>
  );

  const SentenceSection = () => (
    <div style={dynamicStyles.contentSection}>
      <div style={styles.sectionHeader}>
        <h3 style={styles.sectionTitle}>
          <MenuBook sx={{ fontSize: 20, marginRight: 1, color: '#1976d2' }} />
          Recent Sentences
        </h3>
        {data.sentences.length > 10 && (
          <button
            style={styles.toggleButton}
            onClick={() => setShowAllSentences(!showAllSentences)}
            aria-label={showAllSentences ? 'Show top 10 sentences' : 'Show all sentences'}
          >
            {showAllSentences ? <ViewModule sx={{ fontSize: 20 }} /> : <ViewList sx={{ fontSize: 20 }} />}
          </button>
        )}
      </div>
      
      <div style={styles.scrollContainer}>
        {getSentencesToShow().length === 0 ? (
          <p style={styles.emptyText}>No sentences learned yet</p>
        ) : (
          getSentencesToShow().map((sentence, index) => (
            <div 
              key={sentence._id} 
              style={styles.itemContainer}
              onClick={() => handleSentenceClick(sentence, index)}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.04)';
                e.currentTarget.style.cursor = 'pointer';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <div style={styles.itemContent}>
                <p style={styles.sentenceText}>
                  {sentence.text}
                </p>
                <p style={styles.dateText}>
                  {formatDate(sentence.createdAt)}
                </p>
              </div>
              <div style={styles.itemIndicator}>
                <CheckCircle sx={{ fontSize: 16, color: '#4caf50' }} />
              </div>
              {index < getSentencesToShow().length - 1 && (
                <div style={styles.divider} />
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );

  const ConceptSection = () => (
    <div style={dynamicStyles.contentSection}>
      <div style={styles.sectionHeader}>
        <h3 style={styles.sectionTitle}>
          <Translate sx={{ fontSize: 20, marginRight: 1, color: '#dc004e' }} />
          Recent Words
        </h3>
        {data.concepts.length > 10 && (
          <button
            style={styles.toggleButton}
            onClick={() => setShowAllConcepts(!showAllConcepts)}
            aria-label={showAllConcepts ? 'Show top 10 words' : 'Show all words'}
          >
            {showAllConcepts ? <ViewModule sx={{ fontSize: 20 }} /> : <ViewList sx={{ fontSize: 20 }} />}
          </button>
        )}
      </div>
      
      <div style={styles.scrollContainer}>
        {getConceptsToShow().length === 0 ? (
          <p style={styles.emptyText}>No words learned yet</p>
        ) : (
          getConceptsToShow().map((concept, index) => (
            <div 
              key={concept._id} 
              style={styles.itemContainer}
              onClick={() => handleConceptClick(concept, index)}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.04)';
                e.currentTarget.style.cursor = 'pointer';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <div style={styles.itemContent}>
                <p style={styles.conceptText}>
                  {concept.conceptName}
                </p>
                <p style={styles.dateText}>
                  {formatDate(concept.createdAt)}
                </p>
              </div>
              <div style={styles.itemIndicator}>
                <Star sx={{ fontSize: 16, color: '#ff9800' }} />
              </div>
              {index < getConceptsToShow().length - 1 && (
                <div style={styles.divider} />
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );

  // Dynamic styles based on current state
  const dynamicStyles = {
    card: {
      ...styles.card,
      minHeight: isMobile ? 'auto' : '300px',
      maxHeight: isMobile ? 'none' : '300px',
    },
    cardContent: {
      ...styles.cardContent,
      flexDirection: isMobile ? 'column' : 'row',
      minHeight: isMobile ? 'auto' : '300px',
    },
    statsSection: {
      ...styles.statsSection,
      width: isMobile ? '100%' : isTablet ? '100%' : '30%',
      flexDirection: isMobile ? 'row' : isTablet ? 'row' : 'column',
      justifyContent: isMobile ? 'space-around' : isTablet ? 'space-around' : 'center',
      borderRight: isTablet || isMobile ? 'none' : '1px solid rgba(0, 0, 0, 0.08)',
      borderBottom: isTablet || isMobile ? '1px solid rgba(0, 0, 0, 0.08)' : 'none',
      minHeight: isMobile ? '120px' : isTablet ? '140px' : '252px',
    },
    contentSection: {
      ...styles.contentSection,
      width: isMobile ? '100%' : isTablet ? '50%' : '35%',
      borderRight: isMobile ? 'none' : '1px solid rgba(0, 0, 0, 0.08)',
      borderBottom: isMobile ? '1px solid rgba(0, 0, 0, 0.08)' : 'none',
      minHeight: isMobile ? '250px' : 'auto',
    },
    statItem: {
      ...styles.statItem,
      marginBottom: isTablet || isMobile ? '0' : '24px',
      marginRight: isTablet || isMobile ? '12px' : '0',
    },
    statRow: {
      ...styles.statRow,
      flexDirection: isMobile ? 'column' : 'row',
    },
  };

  return (
    <div style={dynamicStyles.card}>
      <div style={dynamicStyles.cardContent}>
        <StatsSection />
        <SentenceSection />
        <ConceptSection />
      </div>
    </div>
  );
};

const styles = {
  card: {
    maxWidth: '1000px',
    width: '100%',
    borderRadius: '16px',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12), 0 4px 8px rgba(0, 0, 0, 0.08)',
    overflow: 'hidden',
    backgroundColor: '#ffffff',
    border: 'none',
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    position: 'relative',
    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
    margin: '0 auto',
  },
  cardContent: {
    display: 'flex',
    height: '100%',
    padding: '0',
  },
  statsSection: {
    padding: '24px',
    display: 'flex',
    alignItems: 'center',
    maxHeight: '300px',
    background: 'linear-gradient(145deg, #f8f9fa 0%, #e9ecef 100%)',
  },
  contentSection: {
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '300px',
  },
  statItem: {
    textAlign: 'center',
    position: 'relative',
  },
  statRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    marginBottom: '12px',
  },
  statIconContainer: {
    position: 'relative',
    display: 'inline-block',
  },
  progressBadge: {
    position: 'absolute',
    top: '-2px',
    right: '-2px',
    backgroundColor: '#ffffff',
    borderRadius: '50%',
    padding: '2px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  statNumber: {
    fontSize: '2rem',
    fontWeight: '600',
    margin: '0 0 4px 0',
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    lineHeight: '1',
  },
  statLabel: {
    fontSize: '0.75rem',
    color: 'rgba(0, 0, 0, 0.7)',
    fontWeight: '500',
    letterSpacing: '0.01071em',
    lineHeight: '1.2',
  },
  trendingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    padding: '4px 8px',
    borderRadius: '12px',
    backgroundColor: 'rgba(76, 175, 80, 0.1)',
  },
  trendingText: {
    fontSize: '0.75rem',
    fontWeight: '500',
    color: '#2e7d32',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    padding: '0 4px',
  },
  sectionTitle: {
    fontSize: '1.1rem',
    fontWeight: '600',
    margin: '0',
    color: 'rgba(0, 0, 0, 0.87)',
    letterSpacing: '0.0075em',
    display: 'flex',
    alignItems: 'center',
  },
  toggleButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#1976d2',
    padding: '8px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
  },
  scrollContainer: {
    flex: 1,
    overflowY: 'auto',
    maxHeight: '200px',
    paddingRight: '4px',
  },
  itemContainer: {
    marginBottom: '12px',
    paddingBottom: '8px',
    borderRadius: '8px',
    padding: '12px',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'relative',
    cursor: 'pointer',
  },
  itemContent: {
    flex: 1,
  },
  itemIndicator: {
    marginLeft: '12px',
    opacity: 0.7,
  },
  sentenceText: {
    fontSize: '0.9rem',
    lineHeight: '1.4',
    margin: '0 0 6px 0',
    wordWrap: 'break-word',
    color: 'rgba(0, 0, 0, 0.87)',
    fontWeight: '400',
    letterSpacing: '0.00938em',
  },
  conceptText: {
    fontSize: '1rem',
    lineHeight: '1.4',
    margin: '0 0 6px 0',
    fontWeight: '600',
    color: 'rgba(0, 0, 0, 0.87)',
    letterSpacing: '0.00938em',
  },
  dateText: {
    fontSize: '0.7rem',
    color: 'rgba(0, 0, 0, 0.6)',
    margin: '0',
    fontWeight: '400',
    letterSpacing: '0.03333em',
  },
  divider: {
    position: 'absolute',
    bottom: '0',
    left: '12px',
    right: '12px',
    height: '1px',
    backgroundColor: 'rgba(0, 0, 0, 0.08)',
  },
  emptyText: {
    fontSize: '0.875rem',
    color: 'rgba(0, 0, 0, 0.6)',
    fontStyle: 'italic',
    margin: '0',
    textAlign: 'center',
    padding: '40px 20px',
    background: 'rgba(0, 0, 0, 0.02)',
    borderRadius: '8px',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '300px',
    gap: '20px',
  },
  spinner: {
    width: '48px',
    height: '48px',
    border: '4px solid #e3f2fd',
    borderTop: '4px solid #1976d2',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    fontSize: '0.875rem',
    color: 'rgba(0, 0, 0, 0.6)',
    margin: '0',
    fontWeight: '500',
  },
  errorContainer: {
    padding: '24px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '300px',
  },
  errorAlert: {
    backgroundColor: '#ffebee',
    border: '1px solid #ffcdd2',
    borderRadius: '8px',
    padding: '20px',
    width: '100%',
    maxWidth: '400px',
  },
  errorText: {
    color: '#d32f2f',
    margin: '0',
    fontSize: '0.875rem',
    textAlign: 'center',
    fontWeight: '500',
  },
  overlay: {
    position: 'fixed',
    top: '0',
    left: '0',
    right: '0',
    bottom: '0',
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    zIndex: 9999,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '20px',
  },
  cardViewerContainer: {
    position: 'relative',
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 24px 48px rgba(0, 0, 0, 0.3)',
    maxWidth: '90vw',
    maxHeight: '90vh',
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    // Reset all color inheritance to prevent blue leaking
    color: 'initial',
    fontFamily: 'initial',
    fontSize: 'initial',
    fontWeight: 'initial',
  },
  cardViewerContent: {
    flex: 1,
    overflow: 'auto',
    padding: '20px',
    // Explicitly reset color inheritance and set default text color
    color: 'rgba(0, 0, 0, 0.87)',
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    fontSize: '14px',
    fontWeight: 'normal',
    lineHeight: '1.43',
    letterSpacing: '0.01071em',
    // Reset all inherited styles
    background: 'initial',
    border: 'initial',
    margin: 'initial',
    // Ensure child elements don't inherit unwanted colors
    '& *': {
      color: 'inherit !important',
    },
  },
  closeButton: {
    position: 'absolute',
    top: '16px',
    right: '16px',
    background: 'rgba(255, 255, 255, 0.95)',
    border: 'none',
    borderRadius: '50%',
    width: '48px',
    height: '48px',
    cursor: 'pointer',
    zIndex: 10001,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#333',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
    transition: 'all 0.2s ease',
  },
};

// Simple CSS for spinner animation
const spinnerStyles = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const styleElement = document.createElement('style');
styleElement.textContent = spinnerStyles;
if (!document.head.querySelector('style[data-flashcard-spinner]')) {
  styleElement.setAttribute('data-flashcard-spinner', 'true');
  document.head.appendChild(styleElement);
}

export default FlashcardProgressCard;